import { LockSimpleIcon, PlusIcon, PrinterIcon } from '@phosphor-icons/react';
import { type ReactNode, useState } from 'react';
import { Button, EmptyState, Modal, Spinner, toast } from '../../ui';
import { AddStationModal } from './AddStationModal';
import { Downloads } from './Downloads';
import { ShipNodeProductionComingSoon } from './links';
import { StationCard } from './StationCard';
import { apiErrorMessage } from './status';
import type {
  ShipNodeChannel,
  ShipNodeStation,
  ShipNodeStationsService,
} from './types';
import { useShipNodeStations } from './useShipNodeStations';

export type ShipNodeTabProps = {
  /** The app's station backend: organization-scoped in the portal, one warehouse in the WMS. */
  service: ShipNodeStationsService;
  /** Which installers the download links point at. */
  channel: ShipNodeChannel;
  /** Add and Remove station. The portal passes the user's admin role; the WMS lets every user. */
  canManage: boolean;
  /** Printer switch and test page: everyone but view-only users. */
  canUse: boolean;
};

/** Settings › ShipNode: pair packing PCs, watch their printers, send a test page. */
export function ShipNodeTab(props: ShipNodeTabProps) {
  /* The production "coming soon" switch (links.ts): while on, the tab only says ShipNode is coming and never calls
     the station routes. */
  if (props.channel === 'production' && ShipNodeProductionComingSoon) {
    return <ShipNodeComingSoon channel={props.channel} />;
  }
  return <ShipNodeStations {...props} />;
}

function ShipNodeComingSoon({ channel }: { channel: ShipNodeChannel }) {
  return (
    <div className="sk:flex sk:max-w-240 sk:flex-col sk:gap-6 sk:py-4">
      <header className="sk:flex sk:flex-col sk:gap-1">
        <h2 className="sk:m-0 sk:text-base sk:font-semibold sk:text-fg">
          ShipNode stations
        </h2>
        <p className="sk:m-0 sk:text-dense sk:text-fg-tertiary">
          Packing PCs that print your shipping labels.
        </p>
      </header>

      <Downloads channel={channel} />
    </div>
  );
}

function ShipNodeStations({
  service,
  channel,
  canManage,
  canUse,
}: ShipNodeTabProps) {
  const { stations, loading, error, refresh } = useShipNodeStations(service);
  const [isAdding, setIsAdding] = useState(false);
  const [removing, setRemoving] = useState<ShipNodeStation | null>(null);
  const [isRemoveBusy, setIsRemoveBusy] = useState(false);

  const closeAdd = (isOpen: boolean) => {
    setIsAdding(isOpen);
    /* A station paired while the code was up shows now, not at the next tick. */
    if (!isOpen) refresh();
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setIsRemoveBusy(true);
    try {
      await service.removeStation(removing.id);
      toast.success(`${removing.name || 'Station'} removed`);
      setRemoving(null);
      await refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not remove the station'));
    } finally {
      setIsRemoveBusy(false);
    }
  };

  const addButton = canManage ? (
    <Button variant="primary" onPress={() => setIsAdding(true)}>
      <PlusIcon size={14} weight="bold" />
      Add station
    </Button>
  ) : null;

  /* Non-admins never see Add / Remove, so say why instead of leaving a gap (Ely, 2026-09-29). */
  const adminNote = canManage ? null : (
    <p
      role="note"
      className="sk:m-0 sk:flex sk:items-center sk:gap-1.5 sk:text-dense sk:text-fg-secondary"
    >
      <LockSimpleIcon size={14} aria-hidden />
      Only admins of your account can add or remove stations.
    </p>
  );

  let body: ReactNode;
  if (loading) {
    body = (
      <div className="sk:flex sk:justify-center sk:py-10">
        <Spinner label="Loading ShipNode stations" />
      </div>
    );
  } else if (error && !stations.length) {
    body = (
      <EmptyState
        title="Couldn't load ShipNode stations"
        description={error}
        action={<Button onPress={refresh}>Try again</Button>}
      />
    );
  } else if (!stations.length) {
    body = (
      <EmptyState
        icon={<PrinterIcon size={20} />}
        title="No ShipNode stations yet"
        description={
          canManage
            ? 'Add a station to print labels from Skupreme on a packing PC.'
            : 'An admin of your account can add a packing PC as a station.'
        }
        action={addButton}
      />
    );
  } else {
    body = (
      <div className="sk:flex sk:flex-col sk:gap-4">
        {stations.map(station => (
          <StationCard
            key={station.id}
            service={service}
            station={station}
            canManage={canManage}
            canUse={canUse}
            onRemove={setRemoving}
            onChanged={refresh}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="sk:flex sk:max-w-240 sk:flex-col sk:gap-6 sk:py-4">
      <header className="sk:flex sk:flex-wrap sk:items-start sk:justify-between sk:gap-3">
        <div className="sk:flex sk:flex-col sk:gap-1">
          <h2 className="sk:m-0 sk:text-base sk:font-semibold sk:text-fg">
            ShipNode stations
          </h2>
          <p className="sk:m-0 sk:text-dense sk:text-fg-tertiary">
            Packing PCs that print your shipping labels. The list refreshes
            every 15 seconds.
          </p>
        </div>
        {stations.length ? addButton : null}
        {adminNote}
      </header>

      {body}

      <Downloads channel={channel} />

      <AddStationModal
        service={service}
        isOpen={isAdding}
        onOpenChange={closeAdd}
      />

      <Modal
        isOpen={removing !== null}
        onOpenChange={isOpen => {
          if (!isOpen) setRemoving(null);
        }}
        isDismissable={!isRemoveBusy}
        title="Remove station?"
        footer={
          <>
            <Button onPress={() => setRemoving(null)} isDisabled={isRemoveBusy}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onPress={confirmRemove}
              isDisabled={isRemoveBusy}
            >
              {isRemoveBusy ? 'Removing…' : 'Remove station'}
            </Button>
          </>
        }
      >
        <p className="sk:m-0">
          {removing?.name || 'This station'} stops printing and ShipNode on that
          PC goes back to its pairing screen. You can add it again with a new
          code.
        </p>
      </Modal>
    </div>
  );
}
